import { useFormikContext } from 'formik';
import { GitBranchIcon, LayoutGrid, PackageIcon } from 'lucide-react';

import {
  EdgeGroupListItemResponse,
  useEdgeGroups,
} from '@/react/edge/edge-groups/queries/useEdgeGroups';
import { useSource } from '@/react/portainer/gitops/sources/queries/useSource';

import { Card } from '@@/primitives/Card';
import { Badge } from '@@/Badge';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';

import { validationSchema } from '../validation';
import { WizardStep } from '../WizardContext';
import { Artifact, FormValues } from '../types';
import { DEPLOYMENT_TYPE_LABELS } from '../detectDeploymentType';

import { StepTitle } from './StepTitle';

export const ReviewStep: WizardStep = {
  id: 'review',
  label: 'Review',
  Component: ReviewStepView,
  validateStep,
};

function ReviewStepView() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const sourceQuery = useSource(values.sources[0]);
  const edgeGroupsQuery = useEdgeGroups();

  return (
    <>
      <StepTitle
        title="Review & name your workflow"
        subtitle="Give this workflow a name and confirm the sources, stacks, and deployments targets below. Everything is created together when you finish."
      />

      <div className="flex flex-col gap-4">
        <FormControl inputId="workflow-name" label="Workflow name" required>
          <Input
            id="workflow-name"
            onChange={(e) => {
              setFieldValue('name', e.target.value);
            }}
            placeholder="my-workflow"
            value={values.name}
            data-cy="workflow-name-input"
            required
          />
        </FormControl>

        <Card.Container aria-label="Source">
          <Card.Header icon={GitBranchIcon} title="Source" />
          <Card.Body>{sourceQuery.data?.name}</Card.Body>
        </Card.Container>

        {values.artifacts.map((artifact, index) => (
          <ArtifactReviewSection
            key={index}
            values={artifact}
            edgeGroups={edgeGroupsQuery.data}
          />
        ))}
      </div>
    </>
  );
}

function ArtifactReviewSection({
  values,
  edgeGroups,
}: {
  values: Artifact;
  edgeGroups: EdgeGroupListItemResponse[] | undefined;
}) {
  const targetNames = (edgeGroups ?? [])
    .filter((group) => values.targets.includes(group.Id))
    .map((group) => group.Name);

  return (
    <>
      <Card.Container aria-label="Stack">
        <Card.Header
          icon={PackageIcon}
          title="Stack"
          subtitle={values.name}
          actions={
            <Badge type="muted">
              {DEPLOYMENT_TYPE_LABELS[values.deploymentType]}
            </Badge>
          }
        />
        <Card.Body>
          {values.files.map((file) => (
            <span key={file.path}>{file.path}</span>
          ))}
        </Card.Body>
      </Card.Container>

      <Card.Container aria-label="Targets">
        <Card.Header
          icon={LayoutGrid}
          title="Targets"
          actions={<Badge type="muted">{targetNames.length}</Badge>}
        />
        <Card.Body>
          {targetNames.map((name) => (
            <span key={name}>{name}</span>
          ))}
        </Card.Body>
      </Card.Container>
    </>
  );
}

function validateStep() {
  return validationSchema();
}
